import { Button } from "@/components/ui/button";
import { memo } from "react";
import { useTranslation } from "react-i18next";

interface FormActionsProps {
  activeTab: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onPrevious: () => void;
  onNext: (e: React.MouseEvent) => void;
  onSave: () => void;
  isLastTab: boolean;
  isCreateMode?: boolean;
}

export const FormActions = memo(({
  activeTab,
  isSubmitting,
  onCancel,
  onPrevious,
  onNext,
  onSave,
  isLastTab,
  isCreateMode = false
}: FormActionsProps) => {
  const { t } = useTranslation();
  
  // Different classes for create vs edit mode
  const containerClass = isCreateMode
    ? "fixed bottom-0 left-0 right-0 bg-muted p-4 border-t flex justify-between items-center w-full z-50"
    : "absolute bottom-0 left-0 right-0 border-t bg-muted p-4 flex justify-between items-center w-full z-50";

  // Helper function to trigger form submission
  const triggerFormSubmit = (e: React.MouseEvent) => {

    e.preventDefault();
    console.log("Triggering form submission");
    
    // Find the closest form element
    const form = (e.target as HTMLElement).closest('form');
    
    if (form) {
      // Create a custom event with a flag that we can check later
      const submitEvent = new CustomEvent('submit', { 
        bubbles: true, 
        cancelable: true,
      });
      
      // Add custom property to identify this as a submit button click
      (submitEvent as any)._isSubmitButtonClick = true;
      
      console.log("Dispatching submit event with _isSubmitButtonClick flag");
      form.dispatchEvent(submitEvent);
    } else {
      console.error("No form element found in the DOM hierarchy");
    }
  };

  return (
    <div className={containerClass}>
      <div className="flex gap-2">
        <Button 
          type="button" 
          onClick={onCancel} 
          disabled={isSubmitting}
        >
          {t("common.cancel")}
        </Button>
      </div>
      
      <div className="flex gap-2">
        
        {activeTab !== "basic" && (
          <Button 
            type="button" 
            onClick={onPrevious}
          >
            {t("jobs.formWizard.previous")}
          </Button>
        )}
        
        {isLastTab ? (
          <Button 
            type="button"
            disabled={isSubmitting}
            onClick={triggerFormSubmit}
          >
            {isSubmitting ? t("common.saving") : isCreateMode ? t("jobs.formWizard.createJob") : t("jobs.formWizard.saveChanges")}
          </Button>
        ) : (
          <Button 
            type="button"
            onClick={onNext}
          >
            {t("jobs.formWizard.next")}
          </Button>
        )}
      </div>
    </div>
  );
});

FormActions.displayName = "FormActions";
